import { useNavigate } from "react-router-dom";
import "./ReportsSectionCompact.css";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import { useAuth } from "../context/AuthContext";

const REPORT_CARDS = [
  {
    key: "registration_analytics",
    title: "Registration Analytics",
    description:
      "Track Ellyanna Booking and Ellyanna Boothshare registrations, roles, sources, locations, and conversion milestones.",
    tooltip:
      "System-wide growth report for registration trends, Ellyanna Boothshare conversions, and trial/billing milestones.",
    to: "/superadmin/reports/registration-analytics",
  },
  {
    key: "login_analytics",
    title: "Login Analytics",
    description:
      "Track Ellyanna Booking and Ellyanna Boothshare logins, access issues, MFA activity, IP addresses, devices, and salon locations.",
    tooltip:
      "System-wide login report for recent sign-ins, blocked attempts, cross-launch activity, device mix, and local-time trends.",
    to: "/superadmin/reports/login-analytics",
  },
  {
    key: "billing_coupon_report",
    title: "Billing Coupon Report",
    description:
      "Track Ellyanna billing coupon redemptions by campaign, salon, business, and status.",
    tooltip:
      "System-wide coupon campaign report using each salon's local redemption date and shared Super Admin location filters.",
    to: "/superadmin/reports/billing-coupons",
  },
  {
    key: "kpi",
    title: "System KPI Snapshot",
    description:
      "Run system-wide KPIs across all salons, salons under one business, or one salon. Includes refunds/voided options and export.",
    tooltip:
      "High-level KPI view for system, business, or salon scope with timezone-aware accounting dates.",
    to: "/superadmin/reports/kpis",
  },
  {
    key: "sales",
    title: "Sales by Employee (System)",
    description:
      "Sales-by-employee across scope with business/salon filters, payment mix, refunds/voided, CSV export.",
    tooltip:
      "Detailed employee sales report with scope filters and Business/Salon visibility in broader views.",
    to: "/superadmin/reports/sales-by-employee",
  },
  {
    key: "staff_performance",
    title: "Staff Performance",
    description:
      "Compare utilization, sales per hour, tips, refunds, and rebooking impact across all salons, one business, or one salon.",
    tooltip:
      "System-wide staff KPIs using salon timezone accounting dates and time-clock hours where available.",
    to: "/superadmin/reports/staff-performance",
  },
  {
    key: "salon_valuation",
    title: "Estimated Salon Value",
    description: "Valuation range + confidence score.",
    tooltip:
      "System-wide valuation view with salon timezone accounting dates. Switch to Salon scope to edit assumptions.",
    to: "/superadmin/reports/salon-valuation",
  },
  {
    key: "break_even_sensitivity",
    title: "Break-Even Sensitivity",
    description: "Break-even revenue + scenario analysis.",
    tooltip:
      "Break-even revenue targets with scenario analysis across all salons, one business, or one salon.",
    to: "/superadmin/reports/break-even-sensitivity",
  },
  {
    key: "live_dashboard",
    title: "Live Dashboard",
    description:
      "A quick snapshot of sales, rebooking, and staff utilization across all salons, one business, or one salon.",
    tooltip: "System-wide KPIs using each salon's local timezone.",
    to: "/superadmin/reports/live-dashboard",
  },
  {
    key: "walk_in_queue",
    title: "Walk-In Queue",
    description:
      "Review walk-in turns, fairness, overrides, and revenue for the active salon.",
    tooltip:
      "Salon-level walk-in queue report available to super admins from the report hub.",
    to: "/superadmin/reports/walk-in-queue",
  },
  {
    key: "profitability_estimate",
    title: "Profitability Estimate",
    description:
      "Estimate monthly profit using revenue and expense assumptions for the active salon.",
    tooltip:
      "Salon-level profitability estimate available to super admins from the report hub.",
    to: "/superadmin/reports/profitability-estimate",
  },
  {
    key: "tax_liability",
    title: "Tax Liability Summary",
    description:
      "Review taxable vs non-taxable sales and estimate tax owed across all salons, one business, or one salon.",
    tooltip:
      "System-wide tax liability estimate using each salon's timezone accounting date.",
    to: "/superadmin/reports/tax-liability",
  },
  {
    key: "total_sheet",
    title: "Total Sheet",
    description:
      "Daily/period summary of sales, tax, refunds, discounts, payments, and employee totals across scope.",
    tooltip: "System-wide Total Sheet with salon timezone accounting dates.",
    to: "/superadmin/reports/total-sheet",
  },
  {
    key: "ticket_summary",
    title: "Ticket Summary",
    description:
      "Summarize ticket sales by employee with totals and payment breakdowns for the active salon.",
    tooltip:
      "Salon-level ticket summary available to super admins from the report hub.",
    to: "/superadmin/reports/ticket-summary",
  },
  {
    key: "daily_closing",
    title: "Daily Closing Cash Report",
    description:
      "Reconcile daily cash, bonus payouts, and expenses against the Total Sheet for the active salon.",
    tooltip:
      "Salon-level daily closing report available to super admins from the report hub.",
    to: "/superadmin/reports/daily-closing",
  },
  {
    key: "cash_balance",
    title: "Cash Balance Report",
    description:
      "Track daily cash revenue, cash expenses, and current cash balance for the active salon.",
    tooltip:
      "Salon-level cash balance report available to super admins from the report hub.",
    to: "/superadmin/reports/cash-balance",
  },
  {
    key: "sales_tickets",
    title: "Sales & Tickets",
    description:
      "Review ticket volume, cancellations, booking sources, peak hours, and conversion trends for the active salon.",
    tooltip:
      "Salon-level sales and tickets report available to super admins from the report hub.",
    to: "/superadmin/reports/sales-tickets",
  },
  {
    key: "gift_card_analytics",
    title: "Gift Card Analytics",
    description:
      "Track gift cards sold, redeemed, balances, and liability across all salons, one business, or one salon.",
    tooltip:
      "System-wide gift card balances and liability using salon timezone dates.",
    to: "/superadmin/reports/gift-card-analytics",
  },
  {
    key: "rewards_analytics",
    title: "Rewards Analytics",
    description:
      "Monitor points earned, redeemed, unused balances, and referrals across all salons, one business, or one salon.",
    tooltip:
      "System-wide rewards metrics using salon timezone accounting dates.",
    to: "/superadmin/reports/rewards",
  },
  {
    key: "cash_drawer_analysis",
    title: "Cash Drawer Analysis",
    description:
      "Review gift card liability, redemption, cash drawer over/short, and paid-in/out activity for the active salon.",
    tooltip:
      "Salon-level cash drawer analysis available to super admins from the report hub.",
    to: "/superadmin/reports/gift-cards-cash-drawer",
  },
  {
    key: "appointment_status",
    title: "Appointment Status Report",
    description:
      "Track scheduled, checked-in, cancelled, and closed appointments across all salons, one business, or one salon.",
    tooltip:
      "System-wide appointment statuses using each salon's local timezone.",
    to: "/superadmin/reports/appointments-status",
  },
  {
    key: "cancellations",
    title: "Cancellations & No-Shows",
    description:
      "Track cancellations, reschedules, and late change attempts across all salons, one business, or one salon.",
    tooltip:
      "System-wide cancellations & no-shows using each salon's timezone.",
    to: "/superadmin/reports/cancellations",
  },
  {
    key: "service_analytics",
    title: "Service Analytics",
    description:
      "Revenue, demand, and time usage by service across all salons, one business, or one salon.",
    tooltip:
      "System-wide service analytics with salon timezone accounting dates.",
    to: "/superadmin/reports/service-analytics",
  },
  {
    key: "maintenance_cleaning_analytics",
    title: "Maintenance & Cleaning Analytics",
    description:
      "Track maintenance completions, overdue risk, and consistency across all businesses, one business, or one salon.",
    tooltip:
      "Scope-aware maintenance analytics with business/salon filters and timezone-safe calculations.",
    to: "/superadmin/reports/maintenance-cleaning",
  },
  {
    key: "category_analytics",
    title: "Category Analytics",
    description:
      "Track revenue mix by service category and trends across all salons, one business, or one salon.",
    tooltip: "System-wide category mix using salon timezone accounting dates.",
    to: "/superadmin/reports/category-analytics",
  },
  {
    key: "client_analytics",
    title: "Client Analytics",
    description:
      "Track new vs returning clients, retention, and rebooking across all salons, one business, or one salon.",
    tooltip:
      "System-wide client analytics using salon timezone accounting dates.",
    to: "/superadmin/reports/client-analytics",
  },
  {
    key: "client_satisfaction",
    title: "Client Satisfaction",
    description:
      "Average rating trends, distribution, and follow-up workflow health across all salons, one business, or one salon.",
    tooltip:
      "System-wide satisfaction metrics using each salon's timezone accounting dates.",
    to: "/superadmin/reports/client-satisfaction",
  },
  {
    key: "complaint_themes",
    title: "Complaint Themes",
    description:
      "Track top complaint themes by staff, channel, day of week, and trend over time across all salons, one business, or one salon.",
    tooltip: "System-wide complaint themes using salon-local dates.",
    to: "/superadmin/reports/complaint-themes",
  },
  {
    key: "marketing_campaigns",
    title: "Marketing Campaign Performance",
    description:
      "Track campaign performance, conversions, and revenue impact for the active salon.",
    tooltip:
      "Salon-level marketing campaign report available to super admins from the report hub.",
    to: "/superadmin/reports/marketing-campaigns",
  },
  {
    key: "email_subscription",
    title: "Email Subscription Stats",
    description:
      "Review client opt-in, opt-out, unsubscribe reasons, and preference totals for the active salon.",
    tooltip:
      "Salon-level email subscription report available to super admins from the report hub.",
    to: "/superadmin/reports/email-subscription",
  },
  {
    key: "training_progress",
    title: "Training Progress",
    description:
      "Track staff training completion, overdue items, and progress by role for the active salon.",
    tooltip:
      "Salon-level training progress report available to super admins from the report hub.",
    to: "/superadmin/reports/training-progress",
  },
  {
    key: "bug_reports",
    title: "Bug Reports",
    description: "Track, triage, and resolve user-submitted bugs.",
    tooltip: "System-wide bug report console with salon-local dates.",
    to: "/superadmin/reports/bug-reports",
  },
];



export default function SuperAdminReportsHome() {
  const navigate = useNavigate();
  const { is_super_admin } = useAuth();
  const sectionCard =
    "rounded-2xl border border-slate-200 bg-white shadow-sm p-6";
  const actionBtn =
    "mt-4 inline-flex items-center rounded-full bg-rose-500 px-4 py-2 text-sm md:text-base font-semibold text-white hover:bg-rose-600";

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/superadmin");
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Super Admin Reports
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6 text-base md:text-lg">
            <div className="rounded-2xl">
              <div className="mb-4">
                <p className="text-base md:text-lg font-semibold text-slate-900">
                  Report Cards
                </p>
                <p className="mt-1 text-sm md:text-base text-slate-600">
                  Each card opens a specific report with its own filters and results.
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {REPORT_CARDS.map((card) => (
                  <div key={card.key} className={sectionCard}>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        {card.title}
                      </h2>
                      <InfoTooltip text={card.tooltip} label={card.title} />
                    </div>
                    <p className="mt-2 text-sm md:text-base text-slate-600">
                      {card.description}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate(card.to)}
                      className={actionBtn}
                    >
                      Open
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
